import * as React from 'react';
import styled from 'styled-components';

import Drawer from 'components/common/Drawer';
import { MarkdownPreview } from 'components/common/MarkdownEditor';
import { Icon, IfPermitted } from 'components/common';
import { Alert, Button } from 'components/bootstrap';
import { Row, Col } from 'common/components';
import type {
  EventProceduresAPIType,
  EventProcedureStepAPIType,
} from 'security-app/hooks/api/eventProceduresAPI.types';
import EventProcedureStepView from 'security-app/components/SecurityEvents/EventProcedures/EventProcedureStepList';

import AssocEntities from './AssocEntities';

import EventProcedureModal from '../EventProcedureModal';

const SectionHeaderRow = styled(Row)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.input.border};
  padding-bottom: ${({ theme }) => theme.spacings.sm};
`;

const RoundButton = styled(Button)`
  border-radius: 4px;
`;

type Props = {
  eventProcedure: EventProceduresAPIType<Array<EventProcedureStepAPIType>>;
  onClose: () => void;
};

function EventProcedureDetailsDrawer({ eventProcedure, onClose }: Props) {
  const [show, setShow] = React.useState(false);
  const [showEditModal, setShowEditModal] = React.useState(false);

  React.useEffect(() => {
    setTimeout(() => setShow(true), 100);
  }, []);

  const onCloseEditModal = () => {
    setShowEditModal(false);
  };

  return (
    <>
      <Drawer
        opened={show}
        onClose={onClose}
        size="lg"
        position="right"
        overlayProps={{ zIndex: '1030' }}
        title={
          <Row $align="center" $justify="space-between" $width="100%">
            {eventProcedure.title || <i>No title set</i>}
            <IfPermitted permissions={[`event_procedures:edit:${eventProcedure.id}`]}>
              <RoundButton onClick={() => setShowEditModal(true)}>
                <Row $align="center" $gap="xs">
                  <Icon name="edit_square" />
                  <span>Edit</span>
                </Row>
              </RoundButton>
            </IfPermitted>
          </Row>
        }>
        <Col>
          <SectionHeaderRow>
            <h5>Description</h5>
          </SectionHeaderRow>
          {eventProcedure.description ? (
            <MarkdownPreview show height={250} noBackground noBorder value={eventProcedure.description} noPadding />
          ) : (
            <Alert style={{ width: '100%' }}>No description set</Alert>
          )}
          <SectionHeaderRow>
            <h5>Associated Entities</h5>
          </SectionHeaderRow>
          {eventProcedure.associated_entities?.entity_count > 0 ? (
            <AssocEntities associatedEntities={eventProcedure.associated_entities} />
          ) : (
            <Alert style={{ width: '100%' }}>No associated entites</Alert>
          )}
          <SectionHeaderRow>
            <h5>Event Procedure Steps</h5>
          </SectionHeaderRow>
          {eventProcedure.steps?.length > 0 ? (
            <Col>
              {eventProcedure.steps.map((step: EventProcedureStepAPIType, index: number) => (
                <EventProcedureStepView key={step.id} step={step} order={index + 1} />
              ))}
            </Col>
          ) : (
            <Alert style={{ width: '100%' }}>No steps added</Alert>
          )}
        </Col>
      </Drawer>
      {showEditModal && <EventProcedureModal onClose={onCloseEditModal} eventProcedureID={eventProcedure.id} />}
    </>
  );
}

export default EventProcedureDetailsDrawer;
